import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { flatMap, uniqBy } from 'es-toolkit';
import i18n from 'i18next';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { filteredCollaborators, getCarriersPayload, getErrorMessageFromAPI, valueToNumber } from '../../utilz';
import { getExtendedCarrierAndBidDataEffect, getFilteredGuidesEffect } from '../RoutingGuide/RoutingGuideEffects';
import {
  createSpotQuoteInError,
  createSpotQuoteInSuccess,
  getCarrierDetailsInError,
  getCarrierDetailsInSuccess,
  getCarrierSearchError,
  getCarrierSearchSuccess,
  getNewSpotQuoteNoError,
  getNewSpotQuoteNoSuccess,
  getPortSearchError,
  getPortSearchSuccess,
  getPostalSearchError,
  getPostalSearchSuccess,
  getSpotQuoteTransportationInError,
  getSpotQuoteTransportationInSuccess,
  setAppLoader,
  setCurrentQuoteCarriers,
  setCurrentQuoteCollaborators,
} from './CommonActions';
import {
  createSpotQuoteEffect,
  getCarrierDetailsEffect,
  getCarrierSearchWithPaginationEffect,
  getNewSpotQuoteNoEffect,
  getPostalSearch,
  getShipmentPortList,
  getSpotQuoteTransportationModesEffect,
  putSpotQuoteBidders,
  putSpotQuoteCollaborator,
  uploadQuoteDocumentEffect,
} from './CommonEffects';
import CommonTypes from './CommonTypes';

function* handlePostalSearch({ payload }) {
  try {
    const { data } = yield call(getPostalSearch, payload);
    yield put(getPostalSearchSuccess(data));
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.postalDataFailed'));
    yield put(getPostalSearchError({ message }));
  }
}

function* handlePortSearch({ payload }) {
  try {
    const { data } = yield call(getShipmentPortList, payload);
    yield put(getPortSearchSuccess(data));
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.portDataFailed'));
    yield put(getPortSearchError({ message }));
  }
}

function* handleCarrierSearch({ payload }) {
  try {
    const { data } = yield call(getCarrierSearchWithPaginationEffect, payload);
    yield put(
      getCarrierSearchSuccess({
        data: data?.data ? data?.data : [],
        totalRecords: valueToNumber(data?.totalRecords),
      }),
    );
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.carrierDataFailed'));
    yield put(getCarrierSearchError({ message }));
  }
}

function* getCarrierDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getCarrierDetailsEffect, payload);
    yield put(
      getCarrierDetailsInSuccess({
        data: data ? data : [],
      }),
    );
  } catch (e) {
    yield put(
      getCarrierDetailsInError({
        message: getErrorMessageFromAPI(e, 'Failed to get carrier details'),
      }),
    );
  }
}

function* getNewSpotQuoteNo() {
  try {
    const { data } = yield call(getNewSpotQuoteNoEffect);
    yield put(getNewSpotQuoteNoSuccess(data));
  } catch (error) {
    yield put(getNewSpotQuoteNoError({ message: getErrorMessageFromAPI(error) }));
  }
}

function* getSpotQuoteTransportationModes() {
  try {
    const { data } = yield call(getSpotQuoteTransportationModesEffect);
    yield put(getSpotQuoteTransportationInSuccess(data));
  } catch (_error) {
    yield put(getSpotQuoteTransportationInError([]));
  }
}

// todo refactor it

function* createSpotQuote({ payload: { handleSuccess, isPartialSave, quoteDocuments, ...payload } }) {
  const collaborators = yield select((state) => state.common.currentSpotCollabarators);
  const carriers = yield select((state) => state.common.currentSpotCarriers);
  let data = null;
  // block for the crate request. We need to set createSpotQuoteInError only if create request drop error
  try {
    yield put(setAppLoader(true));

    const { data: createResponse } = yield call(createSpotQuoteEffect, payload);
    data = createResponse;

    if (data === 0) {
      throw i18n.t('translation:default.notification.errorWithCode0');
    }

    yield put(createSpotQuoteInSuccess({ data }));
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e);

    yield put(setAppLoader(false));
    yield put(createSpotQuoteInError({ message: errorMessage }));

    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: `${errorMessage}`,
    });

    return;
  }

  // block for the putSpotQuoteBidders and putSpotQuoteCollaborator
  try {
    const carrierPayload = {
      quoteKey: data,
      carriers: getCarriersPayload(carriers),
    };

    const collaboratorPayload = {
      spotQuoteId: data,
      collaborators: filteredCollaborators(collaborators),
    };

    yield all({
      carriers: call(putSpotQuoteBidders, carrierPayload),
      collaborators: call(putSpotQuoteCollaborator, collaboratorPayload),
    });

    for (let i = 0; i < quoteDocuments.length; i++) {
      quoteDocuments[i].append('QuoteKey', data);

      yield call(uploadQuoteDocumentEffect, quoteDocuments[i]);
    }

    yield put(setAppLoader(false));

    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Quote created',
    });
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e);

    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: `${errorMessage}`,
    });
  }
}

function* getCurrentQuoteCollaborators() {
  const mapUniqEntities = (collection, propName, iteratee) => {
    return uniqBy(
      flatMap(collection, (item) => item[propName] || []),
      (item) => item[iteratee],
    );
  };

  const filterParams = yield select((state) => state.common.currentSpotRoutingGuidesParams);

  try {
    const { data } = yield call(getFilteredGuidesEffect, filterParams);
    if (data.routingGuides) {
      const collaborators = mapUniqEntities(data.routingGuides, 'collaborators', 'userId');
      const routingCarriers = mapUniqEntities(data.routingGuides, 'carriers', 'carrierId');
      yield put(setCurrentQuoteCollaborators(collaborators));

      let { data: carriers } = yield call(getExtendedCarrierAndBidDataEffect, filterParams);
      carriers = carriers.map((carrier) => {
        const routing = routingCarriers.find((item) => item.carrierId === carrier.carrierKey);
        if (routing) {
          return {
            ...carrier,
            carrierName: routing.transportationProvider,
            email: routing.email,
          };
        }
        return carrier;
      });
      yield put(setCurrentQuoteCarriers(carriers));
    }
  } catch (_e) {
    yield put(setAppLoader(false));
  }
}

function* getCurrentQuoteCarriers() {
  const filterParams = yield select((state) => state.common.currentSpotRoutingGuidesParams);
  try {
    const { data } = yield call(getExtendedCarrierAndBidDataEffect, filterParams);
    yield put(setCurrentQuoteCarriers(data));
  } catch (_e) {
    yield put(setAppLoader(false));
  }
}

export default function* commonSaga() {
  yield takeEvery(CommonTypes.GET_POSTAL_SEARCH_PROGRESS, handlePostalSearch);
  yield takeEvery(CommonTypes.GET_PORT_SEARCH_PROGRESS, handlePortSearch);
  yield takeEvery(CommonTypes.GET_CARRIER_SEARCH_PROGRESS, handleCarrierSearch);
  yield takeEvery(CommonTypes.GET_CARRIER_DETAILS_PROGRESS, getCarrierDetailsWatcher);
  yield takeEvery(CommonTypes.GET_NEW_SPOT_QUOTE_NO_IN_PROGRESS, getNewSpotQuoteNo);
  yield takeLatest(CommonTypes.CREATE_SPOT_QUOTE_PROGRESS, createSpotQuote);
  yield takeLatest(CommonTypes.GET_CURRENT_QUOTE_CARRIERS_LIST, getCurrentQuoteCarriers);
  yield takeLatest(CommonTypes.GET_CURRENT_QUOTE_COLLABORATORS_LIST, getCurrentQuoteCollaborators);
  yield takeLatest(CommonTypes.GET_SPOT_QUOTE_TRANSPORTATION_MODES_IN_PROGRESS, getSpotQuoteTransportationModes);
}
